import i18n from 'i18n';
import {
  CURRENCY_TYPES,
  LIMITED_PAYMENT_DESCRIPTION_SIZE,
  MAX_PAYMENT_DESCRIPTION_SIZE,
  PAYMENT_METHOD,
  PAYMENT_METHODS,
  PAYMENT_PROVIDERS
} from 'components/common/constants';

const getValuesInCorrectOrder = (paymentMethods, order) => {
  const result = [];

  order.forEach((method) => {
    if (paymentMethods.includes(method)) {
      result.push(method);
    }
  });

  paymentMethods.forEach((method) => {
    if (!result.includes(method)) {
      result.push(method);
    }
  });

  return result;
};

export const filteringPaymentMethods = (paymentMethods, providerType = undefined) => {
  if (!paymentMethods) {
    return paymentMethods;
  } else if (providerType && providerType === PAYMENT_PROVIDERS.GRAPHEREX) {
    return PAYMENT_METHODS.filter((method) => method === PAYMENT_METHOD.CRYPTO);
  } else if (providerType && providerType === PAYMENT_PROVIDERS.MANUAL) {
    // Hotfix: MANUAL GBP wallets declare BACS/CHAPS in their payment_methods, but since TRN-7283 the
    // form always derives the actual rail from the sort-code lookup (FASTER_PAYMENTS when the sort
    // code supports it, else SWIFT) — the customer's BACS/CHAPS choice is ignored and the payment is
    // always sent/stored as FASTER_PAYMENTS regardless. Offering BACS/CHAPS in the picker is
    // therefore misleading; drop them here so neither the picker nor the auto-selected default method
    // (both driven by this filtered list) ever offers them. Backend still accepts BACS/CHAPS via the
    // API directly — this only affects the frontend picker.
    return PAYMENT_METHODS.filter(
      (method) => paymentMethods.includes(method) && method !== PAYMENT_METHOD.BACS && method !== PAYMENT_METHOD.CHAPS
    );
  } else {
    return PAYMENT_METHODS.filter((method) => paymentMethods.includes(method));
  }
};

export const sortingPaymentMethods = (paymentMethods, currency, exampleArrays, isNonIban, providerType) => {
  if (!paymentMethods || paymentMethods.length < 2) return paymentMethods;

  let exampleArr;
  if (currency === CURRENCY_TYPES.EUR) {
    exampleArr = exampleArrays.EUR;
  } else if (currency === CURRENCY_TYPES.GBP) {
    exampleArr = exampleArrays.GBP;
  } else if (providerType === PAYMENT_PROVIDERS.GRAPHEREX) {
    exampleArr = exampleArrays.CRYPTO;
  } else {
    exampleArr = exampleArrays.OTHER;
  }

  if (isNonIban) {
    exampleArr = exampleArr.filter(
      (method) => method !== PAYMENT_METHOD.INTERNAL && method !== PAYMENT_METHOD.INTERNAL_PROVIDER
    );
  }

  if (providerType === PAYMENT_PROVIDERS.GRAPHEREX) {
    return exampleArr;
  }

  // For CONVERA_API with feature flag, only return methods that are in the exampleArr
  // This filters out SEPA and FASTER_PAYMENTS when they're not in the hidden constant
  if (providerType === PAYMENT_PROVIDERS.CONVERA_API) {
    const filteredMethods = paymentMethods.filter((method) => exampleArr.includes(method));
    return getValuesInCorrectOrder(filteredMethods, exampleArr);
  }

  return getValuesInCorrectOrder(paymentMethods, exampleArr);
};

export const moveSelectedMethodToFront = (paymentMethods, selectedMethod) => {
  return [selectedMethod, ...paymentMethods.filter((item) => item !== selectedMethod)];
};

export const getPaymentMethodsOptions = (paymentMethods, providerType) => {
  const filteredPaymentMethods = filteringPaymentMethods(paymentMethods, providerType);

  return filteredPaymentMethods?.reduce((results, method) => {
    if (filteredPaymentMethods.includes(method)) {
      results.push({ key: method, value: i18n.getMessage(`transfer.paymentMethod.${method}`) });
    }

    return results;
  }, []);
};

export const filterPaymentAccounts = (accounts) => {
  return accounts?.filter((account) => account.transfer_provider !== PAYMENT_PROVIDERS.WALLESTER);
};

export const getMaxDescriptionSize = (paymentMethod) => {
  return paymentMethod === PAYMENT_METHOD.FASTER_PAYMENTS
    ? LIMITED_PAYMENT_DESCRIPTION_SIZE
    : MAX_PAYMENT_DESCRIPTION_SIZE;
};

export const findUserAccount = (userAccounts, ibanToCheck, currentAccountCurrency) => {
  let foundedAccount = null;

  if (!userAccounts || !ibanToCheck || userAccounts.length === 0) {
    return foundedAccount;
  }

  const accountsWithSameNumber = userAccounts.filter(
    (account) =>
      (account.iban && account.iban?.iban === ibanToCheck) ||
      (account.multi_iban && account.multi_iban?.iban === ibanToCheck)
  );

  if (accountsWithSameNumber.length === 1) {
    foundedAccount = accountsWithSameNumber[0];
  } else if (accountsWithSameNumber.length > 1) {
    foundedAccount = accountsWithSameNumber.find((account) => account.currency !== currentAccountCurrency);
  }

  return foundedAccount;
};

export const mapIbanCredentials = (ibanCredentials) => {
  if (!ibanCredentials) return null;
  return {
    bank_address: ibanCredentials?.bankAddress,
    bank_country: ibanCredentials?.bankCountry,
    bank_name: ibanCredentials?.bankName,
    bic: ibanCredentials?.bic,
    account_number: ibanCredentials?.accountNumber,
    sort_code: ibanCredentials?.sortCode,
    payment_methods: ibanCredentials?.paymentMethods
  };
};
